import { Col, InputNumber } from 'antd'
import { FC, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerButtonCircle, { PerButtonCircleProps } from '../../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { PerModal } from '../../../components/custom_components/Modal'
import PerText from '../../../components/custom_components/Text'
import { setCutoffDayForCountry } from '../../../graphql/countrySettings/MutationFunctions'
import useCountrySettings from '../../../graphql/countrySettings/hooks/useCountrySettings'
import { Country } from '../../../types'
import { useHrSupportStore } from './HrSupportStore'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import { PerCollapse } from '../../../components/custom_components/Collapse'
import { showNotification } from '../../../components/custom_components/Notification/notification'

interface InfoLabelAndTextProps {
  label?: string
  text?: string
  labelClassName?: string
  textClassName?: string
  rowClassName?: string
}

interface InfoLabelAndTextAndButtonProps extends InfoLabelAndTextProps, PerButtonCircleProps {}

export const HrSupportCountrySettingsTab: FC = () => {
  const [selected] = useHrSupportStore((state) => [state.selected], shallow)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isErrorMessageVisible, toggleErrorMessageVisible] = useState(false)
  const [t] = useTranslation()
  const {
    loading: countrySettingsLoading,
    error: countrySettingsError,
    data: countrySettingsData,
  } = useCountrySettings(selected.value as Country)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [newCutoffDay, setNewCutoffDay] = useState<number | undefined>(undefined)

  if (countrySettingsLoading || countrySettingsError || !countrySettingsData || !countrySettingsData.getCountrySettings) {
    return (
      <LoadingAndErrors
        isLoading={countrySettingsLoading}
        hasNoData={!countrySettingsData || !countrySettingsData.getCountrySettings}
        error={countrySettingsError}
      />
    )
  }

  const countrySettings = countrySettingsData.getCountrySettings

  const handleButtonClick = () => {
    setIsModalVisible(true)
  }

  const handleModalOk = async () => {
    if (newCutoffDay) {
      setIsSubmitting(true)
      const { errors } = await setCutoffDayForCountry(countrySettings.country, newCutoffDay)
      setIsSubmitting(false)
      if (errors) {
        toggleErrorMessageVisible(true)
      } else {
        setIsModalVisible(false)
        toggleErrorMessageVisible(false)
        setNewCutoffDay(undefined)
        showNotification({
          type: 'success',
          message: t('companySettingsView.countrySettingsTab.notifications.success.title'),
          description: t('companySettingsView.countrySettingsTab.notifications.success.description'),
        })
      }
    }
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    toggleErrorMessageVisible(false)
    setNewCutoffDay(undefined)
  }

  const InfoLabelAndTextAndButton: FC<InfoLabelAndTextAndButtonProps> = ({ ...props }) => {
    return (
      <>
        <PerFlexRow align="middle" className={props.rowClassName && `per-companySettings--appraisalSeason-header-${props.rowClassName}`}>
          <PerText mr="l" className={props.labelClassName && `per-companySettings--appraisalSeason-header-${props.labelClassName}`}>
            {props.label}
          </PerText>

          <PerText mr="l" className={props.textClassName && `per-companySettings--appraisalSeason-header-${props.textClassName}`}>
            {props.text}
          </PerText>

          <PerButtonCircle
            icon={props.icon}
            color={props.color}
            variant={props.variant}
            tooltip={props.tooltip}
            onClick={props.onClick}
            style={{ marginLeft: '0' }}
          />
        </PerFlexRow>
      </>
    )
  }

  const CountrySettingsGuidelinesCollapse = () => {
    const [collapsed, setCollapsed] = useState<boolean>(false)
    const [t] = useTranslation()

    return (
      <BackgroundBox r noShadow>
        <PerCollapse
          defaultActiveKey={collapsed ? -1 : 0}
          onChange={() => {
            if (collapsed) {
              setCollapsed(false)
            } else {
              setCollapsed(true)
            }
          }}
          panels={[
            {
              headingElement: <PerText appearance="tile-heading">{t('companySettingsView.countrySettingsTab.guidelines')}</PerText>,
              content: (
                <ul>
                  <li>
                    <Trans i18nKey="companySettingsView.countrySettingsTab.cutoffDay.cutoffDayDescription" components={[<b></b>]} />
                  </li>
                </ul>
              ),
            },
          ]}
        />
      </BackgroundBox>
    )
  }

  return (
    <>
      <PerInfoBox>
        <InfoBoxTile
          heading={t('companySettingsView.countrySettingsTab.heading')}
          contentFlex="growShrink"
          headingClassName="per-companySettings--appraisalSeason-header-heading"
        >
          <PerFlexRow direction="column" width="50%">
            <InfoLabelAndTextAndButton
              label={t('companySettingsView.countrySettingsTab.cutoffDay.heading', { cutoffDay: countrySettings.cutOffDay })}
              labelClassName="grayText"
              text={countrySettings.cutOffDay.toString()}
              textClassName="text"
              rowClassName="textRow"
              icon="edit_fill"
              color="primary"
              variant="pastel"
              tooltip={t('tooltip.edit')}
              onClick={handleButtonClick}
            ></InfoLabelAndTextAndButton>
          </PerFlexRow>
        </InfoBoxTile>
      </PerInfoBox>
      <CountrySettingsGuidelinesCollapse />
      <PerModal
        title={{ heading: t('companySettingsView.countrySettingsTab.cutoffDay.modal.editCutoffDay') }}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        footer={{
          confirmBtn: {
            onClick: handleModalOk,
            disabled: newCutoffDay === undefined || newCutoffDay === countrySettings.cutOffDay || isErrorMessageVisible,
            isProcessing: { active: isSubmitting },
          },
          cancelBtn: { onClick: handleModalCancel },
        }}
      >
        <PerFlexRow mb="xxxs">
          <Col span={12}>
            <PerText color="secondary">{t('companySettingsView.countrySettingsTab.cutoffDay.modal.cutoffDayCurrentValueLabel')}</PerText>
          </Col>
          <Col span={12}>
            <PerText color="secondary">{t('companySettingsView.countrySettingsTab.cutoffDay.modal.cutoffDayNewValueLabel')}</PerText>
          </Col>
        </PerFlexRow>
        <PerFlexRow align="middle">
          <Col span={12}>
            <PerText>{countrySettings.cutOffDay}</PerText>
          </Col>
          <Col span={12}>
            <InputNumber
              style={{ width: '70%' }}
              min={0}
              max={31}
              value={newCutoffDay}
              onChange={(value) => setNewCutoffDay(value ? value : undefined)}
              placeholder={t('companySettingsView.countrySettingsTab.cutoffDay.modal.cutoffDayPlaceholder')}
            />
          </Col>
        </PerFlexRow>
        {isErrorMessageVisible && (
          <PerText mt="m" color="error">
            {t('companySettingsView.countrySettingsTab.cutoffDay.modal.cutoffDayErrorMessage')}
          </PerText>
        )}
      </PerModal>
    </>
  )
}
